import { useQuery, useMutation } from "@tanstack/react-query";
import { Plus, Edit, Trash2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useState } from "react";

export default function ManageQuizzes() {
  const { toast } = useToast();
  const [quizToDelete, setQuizToDelete] = useState<any>(null);
  
  const { data: quizzes, isLoading } = useQuery({
    queryKey: ['/quizzes'],
  });

  const deleteQuizMutation = useMutation({
    mutationFn: async (quizId: string) => {
      const response = await apiRequest("DELETE", `/quizzes/${quizId}`);
      return response.json();
    },
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Quiz deleted successfully",
      });
      queryClient.invalidateQueries({ queryKey: ['/quizzes'] });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  if (isLoading) {
    return <div className="text-center py-8">Loading quizzes...</div>;
  }

  return (
    <div className="space-y-6" data-testid="manage-quizzes">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-servify-dark-grey">Manage Quizzes</h1>
          <p className="text-servify-dark-grey/70">Create and manage knowledge assessment quizzes</p>
        </div>
        <Button data-testid="button-add-quiz">
          <Plus className="mr-2 h-4 w-4" />
          Create Quiz
        </Button>
      </div>

      <div className="grid gap-6">
        {(quizzes as any)?.quizzes?.length > 0 ? (
          (quizzes as any).quizzes.map((quiz: any) => (
            <Card key={quiz.id} data-testid={`quiz-card-${quiz.id}`}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>{quiz.title}</CardTitle>
                    <p className="text-sm text-servify-dark-grey/70">{quiz.questions?.length || 0} questions</p>
                  </div>
                  <div className="flex space-x-2">
                    <Button variant="outline" size="sm" data-testid={`button-edit-quiz-${quiz.id}`}>
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setQuizToDelete(quiz)}
                      disabled={deleteQuizMutation.isPending}
                      data-testid={`button-delete-quiz-${quiz.id}`}
                    >
                      <Trash2 className="h-4 w-4 text-servify-red" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-servify-dark-grey/70">{quiz.description}</p>
              </CardContent>
            </Card>
          ))
        ) : (
          <Card>
            <CardContent className="text-center py-12">
              <p className="text-servify-dark-grey/50">No quizzes created yet.</p>
              <Button className="mt-4" data-testid="button-create-first-quiz">
                <Plus className="mr-2 h-4 w-4" />
                Create Your First Quiz
              </Button>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!quizToDelete} onOpenChange={(open) => !open && setQuizToDelete(null)}>
        <AlertDialogContent className="bg-white border-2 border-gray-300 shadow-2xl max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-2xl font-bold text-gray-900">Delete Quiz</AlertDialogTitle>
            <AlertDialogDescription className="text-base text-gray-700 mt-3 leading-relaxed">
              Are you sure you want to delete <span className="font-semibold text-gray-900">"{quizToDelete?.title}"</span>? This action cannot be undone and will permanently remove the quiz and all associated data.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="mt-6">
            <AlertDialogCancel 
              onClick={() => setQuizToDelete(null)}
              className="bg-gray-100 hover:bg-gray-200 text-gray-800 border-2 border-gray-300 font-semibold px-6"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (quizToDelete) {
                  deleteQuizMutation.mutate(quizToDelete.id);
                  setQuizToDelete(null);
                }
              }}
              className="bg-red-600 hover:bg-red-700 text-white font-semibold px-6 shadow-lg"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}